import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { useTheme } from "@/context/ThemeContext";
import { GroupMembership, HomeGroups } from "@/types/pbsLive/homeGroups";
import { ChevronDown } from "lucide-react";
import { useContext, useEffect, useMemo, useState } from "react";
import { CurrentUserContext } from "@/context/CurrentUserContext";
import { ANALYST_ROLES } from "@/types/constants/constants";
interface IPbsLiveGroupsCardProps {
    groups: HomeGroups | null;
    treatmentGroups: GroupMembership[];
    encryptedUsername: string;
}

const dropdownTabClass = (active: boolean) =>
    `inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium rounded-sm transition-all ${active
        ? "bg-background text-foreground shadow-sm"
        : "text-muted-foreground hover:text-foreground hover:bg-background/50"
    }`;

const PbsLiveGroupsCard = (props: IPbsLiveGroupsCardProps) => {
    const [selectedTab, setSelectedTab] = useState<string>("");
    const [selectedDepartmentGroup, setSelectedDepartmentGroup] = useState<GroupMembership | null>(null);
    const [selectedTreatmentGroup, setSelectedTreatmentGroup] = useState<GroupMembership | null>(null);
    const [selectedParentGroup, setSelectedParentGroup] = useState<GroupMembership | null>(null);
    const [url, setUrl] = useState<string>("");
    const { isDarkMode } = useTheme();
    const { currentUser, isOnBehalfOf } = useContext(CurrentUserContext);

    const departmentGroups = useMemo(() => props.groups?.departmentGroups.memberOf ?? [], [props.groups]);
    const welcomeGroup = useMemo(() => props.groups?.welcomeGroups.primaryUserOf?.[0] ?? null, [props.groups]);
    const parentGroups = useMemo(() => {
        let fullTeam : GroupMembership[] = props.groups?.fullTeamCoordination.memberOf ?? [];
        let analystTeam : GroupMembership[] = props.groups?.analystCoordination.memberOf ?? [];
        analystTeam = (analystTeam.length > 0 && (currentUser.Leadership || ANALYST_ROLES.includes(currentUser.Role))) ? analystTeam : []
        return fullTeam.concat(analystTeam)
    }, [props.groups, currentUser])
    const hasTreatmentGroups = props.treatmentGroups.length > 0;
    const hasParentGroups = parentGroups.length > 0;
    const hasMultipleDepartments = departmentGroups.length > 1;

    useEffect(() => {
        if (props.groups == null) return;
        if (welcomeGroup) {
            setSelectedTab("welcomeGroups");
        } else if (departmentGroups.length > 0) {
            setSelectedDepartmentGroup(departmentGroups[0]);
            setSelectedTab("departmentGroups");
        }
    }, [props.groups, departmentGroups, welcomeGroup])

    useEffect(() => {
        const theme = isDarkMode ? 'dark' : 'light';
        const base = `https://pbs-live-embedded-five.vercel.app?username=${encodeURIComponent(props.encryptedUsername)}&theme=${theme}&readOnInteractionOnly=true`;

        if (selectedTab === "treatmentGroups") {
            const group = selectedTreatmentGroup ?? props.treatmentGroups[0] ?? null;
            setUrl(group ? `${base}&grouptype-id=1&identifier=C${group.roomId}` : "");
            return;
        }
        if (selectedTab === "departmentGroups") {
            const group = selectedDepartmentGroup ?? departmentGroups[0] ?? null;
            setUrl(group ? `${base}&grouptype=${encodeURIComponent("Department Group")}&room-id=${encodeURIComponent(group.roomId)}&title=${encodeURIComponent(group.displayname)}` : "");
            return;
        }
        if (selectedTab === "welcomeGroups" && welcomeGroup) {
            setUrl(`${base}&grouptype=${encodeURIComponent("Welcome Group")}&room-id=${encodeURIComponent(welcomeGroup.roomId)}&title=${encodeURIComponent(welcomeGroup.displayname)}`);
            return;
        }
        if (selectedTab === "parentGroups") {
            const group = selectedParentGroup ?? null;
            setUrl(group ? `${base}&room-id=${encodeURIComponent(group.roomId)}&title=${encodeURIComponent(group.displayname)}` : "");
            return;
        }
        setUrl("");
    }, [selectedTab, selectedDepartmentGroup, selectedTreatmentGroup, selectedParentGroup, isDarkMode, props.encryptedUsername, props.treatmentGroups, departmentGroups, welcomeGroup])

    if (!props.groups) return null;
    return (
        <div className="m-2 md:m-8 p-4 max-h-[calc(80vh)] flex flex-col rounded-lg border border-border bg-card">
            <span className="text-lg font-semibold mb-4">My Groups</span>
            <Tabs value={selectedTab} onValueChange={setSelectedTab}>
                <TabsList className='mb-2'>
                    {/* Department Groups */}
                    {departmentGroups.length === 1 && (
                        <TabsTrigger value="departmentGroups">My Department</TabsTrigger>
                    )}
                    {hasMultipleDepartments && (
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <button className={dropdownTabClass(selectedTab === "departmentGroups")}>
                                    Department Groups
                                    <ChevronDown className="h-3 w-3" />
                                </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent>
                                {departmentGroups.map((group) => (
                                    <DropdownMenuItem
                                        key={group.roomId}
                                        onClick={() => {
                                            setSelectedDepartmentGroup(group);
                                            setSelectedTab("departmentGroups");
                                        }}
                                    >
                                        {group.displayname}
                                    </DropdownMenuItem>
                                ))}
                            </DropdownMenuContent>
                        </DropdownMenu>
                    )}

                    {/* Welcome Group */}
                    {welcomeGroup && (
                        <TabsTrigger value="welcomeGroups">Welcome Group</TabsTrigger>
                    )}

                    {/* Treatment Groups */}
                    {hasTreatmentGroups && (
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <button className={dropdownTabClass(selectedTab === "treatmentGroups")}>
                                    Treatment Groups
                                    <ChevronDown className="h-3 w-3" />
                                </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent>
                                {props.treatmentGroups.map((group) => (
                                    <DropdownMenuItem
                                        key={group.roomId}
                                        onClick={() => {
                                            setSelectedTreatmentGroup(group);
                                            setSelectedTab("treatmentGroups");
                                        }}
                                    >
                                        {group.displayname}
                                    </DropdownMenuItem>
                                ))}
                            </DropdownMenuContent>
                        </DropdownMenu>
                    )}

                    {/* Parent Groups */}
                    {hasParentGroups && (
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <button className={dropdownTabClass(selectedTab === "parentGroups")}>
                                    Parent Groups
                                    <ChevronDown className="h-3 w-3" />
                                </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent>
                                {parentGroups.map((group) => (
                                    <DropdownMenuItem
                                        key={group.roomId}
                                        onClick={() => {
                                            setSelectedParentGroup(group);
                                            setSelectedTab("parentGroups");
                                        }}
                                    >
                                        {group.displayname}
                                    </DropdownMenuItem>
                                ))}
                            </DropdownMenuContent>
                        </DropdownMenu>
                    )}
                </TabsList>
            </Tabs>
            {selectedTab !== '' && url !== '' && isOnBehalfOf && (
                <div className="relative h-[45vh] rounded-lg overflow-hidden">
                    <div className="absolute inset-0 bg-muted/40 backdrop-blur-md rounded-lg z-10 flex flex-col items-center justify-center gap-2 text-center px-6">
                        <span className="text-base font-semibold text-foreground">Groups unavailable while impersonating</span>
                        <span className="text-sm text-muted-foreground">You cannot view group chats on behalf of another user.</span>
                    </div>
                </div>
            )}
            {selectedTab !== '' && url !== '' && !isOnBehalfOf && (
                <iframe
                    title='PBS Live Group Room'
                    className='h-[45vh] rounded-lg'
                    src={url} />
            )}
        </div>
    )
}
export default PbsLiveGroupsCard;
